'use client';
import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { NotInStockDialog } from '@eliadmin/core/src/components/product/dialog/not-in-stock-dialog';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  hasDimensions,
  isSellable,
  getProductMinQuantity,
  getProductQuantityRules,
  getProductQuantityStep,
  normalizeProductQuantity,
  normalizeProductQuantityDown,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useEffect, useMemo } from 'react';
import { LuMinus, LuPlus } from 'react-icons/lu';
import { ProductComparisonButton } from '../product-comparison-button';

export function ProductQuantity() {
  const product = useVariantsStore(s => s.product);
  const hasSpecialProductStatus = product.status === 'ended' || product.status === 'arrival';
  const settings = useGlobalStore(state => state.templateSettings);

  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const customValuesError = useVariantsStore(state => state.customValuesError);

  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);
  const { t } = useTranslations();

  const setSelectedQuantity = useVariantsStore(state => state.setSelectedQuantity);
  const openDialog = useDialogStore(s => s.open);

  //   const selectedServices = useProductStore(state => state.selectedServices);
  const variants = useVariantsStore(state => state.product.variants);
  //   const open = useModalStore(state => state.open);
  //   const close = useModalStore(state => state.close);
  //   const api = useGlobalStore(state => state.config.api);
  //   const decimalPlaces = countDecimals(product.custom_quantity);

  const sellable = isSellable(selectedVariant ? selectedVariant : product, selectedQuantity);
  const quantityRules = useMemo(
    () => getProductQuantityRules(product, selectedVariant),
    [product, selectedVariant],
  );
  const minQuantity = useMemo(() => getProductMinQuantity(quantityRules), [quantityRules]);
  const step = useMemo(() => getProductQuantityStep(quantityRules), [quantityRules]);

  const disabled =
    (product.quantity < 1 && !sellable) ||
    (selectedVariant && selectedVariant?.quantity < 1 && !sellable) ||
    ((variants?.length || 0) > 0 && !selectedVariant);

  const sortedSuppliers = [...(product.suppliers ?? [])].sort((a, b) => a.position - b.position);

  // main affiliate supplier is only when there is only one supplier
  const firstSupplier = sortedSuppliers[0];

  const handleChangeQuantity = (value: number) => {
    if (!value) {
      value = 1;
    }
    const newValue = normalizeProductQuantity(selectedQuantity + value, quantityRules);
    if (newValue <= minQuantity) {
      setSelectedQuantity(minQuantity);
    } else {
      const stock = selectedVariant ?? product;
      const sellableNext = isSellable(stock, newValue);
      if (!sellableNext && stock.quantity > 0) {
        openDialog({
          id: NotInStockDialog.id,
          content: <NotInStockDialog maxQuantity={stock.quantity} unit={product.unit} />,
        });
        setSelectedQuantity(normalizeProductQuantityDown(stock.quantity, quantityRules));
      } else if (sellableNext) {
        setSelectedQuantity(newValue);
      }
    }
  };

  useEffect(() => {
    const tmp = selectedVariant ? selectedVariant : product;
    if (tmp.quantity <= 0 && tmp.availability?.sellable) {
      setSelectedQuantity(minQuantity);
    }
  }, [product, selectedVariant, setSelectedQuantity, minQuantity]);

  useEffect(() => {
    if (selectedQuantity < minQuantity) {
      setSelectedQuantity(minQuantity);
    }
  }, [minQuantity, selectedQuantity, setSelectedQuantity]);

  if (hasSpecialProductStatus) return null;

  return (
    <div className={cn('flex w-full flex-col gap-2 sm:flex-row', 'ea-product-detail-quantity')}>
      {!firstSupplier?.is_affiliate && (
        <div className="flex w-full items-center justify-center gap-2 rounded-full border border-border px-6 sm:w-fit">
          <Button
            aria-label={`${t('text22')} −`}
            variant="none"
            size={'none'}
            className={cn(
              'ea-product-detail-quantity__substract-button ea-product-detail-quantity__button h-6 w-6',
            )}
            disabled={selectedQuantity <= minQuantity}
            onClick={() => {
              handleChangeQuantity(-step);
            }}
          >
            <LuMinus size={20} strokeWidth={2} className="text-primary" />
          </Button>
          <div className="h-12 w-fit flex-grow justify-center">
            <Input
              aria-label={t('text22')}
              inputMode="numeric"
              className={{
                wrapper:
                  'flex-grow-1 h-12 w-full rounded-none bg-transparent sm:w-5 sm:max-w-5 sm:flex-grow-0',
                main: 'flex-grow-1 h-12 w-full text-center sm:w-5 sm:max-w-5',
              }}
              value={selectedQuantity}
              onChange={e => {
                const value = parseInt(e.target.value);
                if (value) {
                  handleChangeQuantity(value - selectedQuantity);
                }
              }}
              onFocus={e => {
                e.target.select();
              }}
            />
          </div>
          <Button
            aria-label={`${t('text22')} +`}
            variant="none"
            size={'none'}
            className={cn(
              'ea-product-detail-quantity__increase-button ea-product-detail-quantity__button h-6 w-6',
            )}
            // disabled={selectedQuantity >= product.quantity}
            onClick={() => {
              handleChangeQuantity(step);
            }}
          >
            <LuPlus size={20} strokeWidth={2} className="text-primary" />
          </Button>
        </div>
      )}
      <div
        className={cn(
          'flex w-full items-center justify-start gap-2',
          'ea-product-detail-quantity__add-to-cart-container',
        )}
      >
        {firstSupplier?.is_affiliate ? (
          <Button
            asChild
            className={cn(
              'h-12 w-full flex-grow sm:max-w-[250px]',
              'ea-product-detail-quantity__go-to-supplier-button',
            )}
          >
            <a href={firstSupplier.url ?? '#'}>{t('text721')}</a>
          </Button>
        ) : (
          <AddToCartButton
            className={cn(
              'h-12 w-full max-w-[600px] flex-grow sm:w-full sm:max-w-[600px]',
              'ea-product-detail-quantity__add-to-cart-button',
            )}
            quantity={selectedQuantity}
            disabled={disabled || (hasDimensions(product.attributes) ? customValuesError : false)}
            product={product}
            sellable={sellable}
            showIcon={true}
            skipVariantDialog={settings.product_detail?.skip_variant_dialog?.enabled}
          />
        )}
        {settings.product_detail?.add_to_favorite?.enabled && (
          <AddToFavoritesButton
            slug={product.slug}
            isFavorite={product.is_favorite ?? false}
            productId={product.id}
            buttonSize={'icon'}
          />
        )}
        <ProductComparisonButton product={product as any} />
      </div>
    </div>
  );
}
